/** 依存を増やさずに済ませるための最小の DOM ヘルパ。 */

/**
 * 図を縦に積み替える幅（px）。styles.css の `@media (max-width: 40rem)` と同じ境目。
 * 引用グラフの列配置は最小でも 780px を要り、概念図の横並びも狭い幅では文字が潰れる。
 * 画面幅ではなくコンテナ幅で判定する（設定画面の幅など、同じ端末でも器の幅は違う）。
 */
export const NARROW_LAYOUT_MAX_WIDTH = 640;

type Child = Node | string | number | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean | undefined | null | EventListener> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  applyAttrs(node, attrs);
  append(node, children);
  return node;
}

export function svg(tag: string, attrs: Record<string, string | number | undefined> = {}, ...children: Child[]): SVGElement {
  const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined) continue;
    node.setAttribute(key, String(value));
  }
  append(node, children);
  return node;
}

function applyAttrs(node: HTMLElement, attrs: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
      continue;
    }
    if (key === 'class') {
      node.className = String(value);
      continue;
    }
    if (key === 'html') {
      // 呼び出し側で必ずエスケープ済みの内容のみを渡す。
      node.innerHTML = String(value);
      continue;
    }
    if (key === 'style' && typeof value === 'string') {
      node.setAttribute('style', value);
      continue;
    }
    node.setAttribute(key, value === true ? '' : String(value));
  }
}

function append(node: Node, children: Child[]): void {
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    node.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** 読み込み中・空・失敗の共通表示。 */
export function stateMessage(icon: string, title: string, detail?: string, action?: HTMLElement): HTMLElement {
  return el(
    'div',
    { class: 'state', role: 'status' },
    el('span', { class: 'state__icon', 'aria-hidden': 'true' }, icon),
    el('p', { class: 'state__title' }, title),
    detail ? el('p', {}, detail) : null,
    action ?? null,
  );
}

export function skeletonCards(count: number): HTMLElement {
  const grid = el('div', { class: 'paper-grid' });
  for (let i = 0; i < count; i += 1) {
    grid.appendChild(
      el(
        'div',
        { class: 'paper-card', 'aria-hidden': 'true' },
        el('div', { class: 'skeleton', style: 'height:0.8rem;width:40%' }),
        el('div', { class: 'skeleton', style: 'height:1.4rem;width:92%' }),
        el('div', { class: 'skeleton', style: 'height:1.4rem;width:70%' }),
        el('div', { class: 'skeleton', style: 'height:0.8rem;width:55%' }),
      ),
    );
  }
  return grid;
}

export function formatDate(value: string | undefined | null): string {
  if (!value) return '日付不明';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
}

export function formatSeconds(seconds: number | undefined): string {
  if (seconds === undefined || !Number.isFinite(seconds)) return '--:--';
  const total = Math.round(seconds);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
